/**
 * Shopify Media Service
 * 
 * This service handles all media-related operations including image uploads,
 * media status checks, and variant-media associations for Shopify products.
 */

import { ShopifyGraphQLService } from './shopifyGraphQLService';

export class ShopifyMediaService {
  constructor(shop, accessToken) {
    this.graphqlService = new ShopifyGraphQLService(shop, accessToken);
  }

  /**
   * Uploads product media (images) to Shopify
   * @param {string} productId - The ID of the product
   * @param {Array} images - Array of image inputs
   * @param {Object} options - Additional options
   * @returns {Promise<Array>} Created media objects
   */
  async uploadProductMedia(productId, images, options = {}) {
    console.log('Preparing to upload media for product:', productId);
    console.log('Initial image data:', images);
    
    // First create a map to store original IDs for tracking
    const originalIdMap = new Map();
    
    const mediaItems = images.map(image => {
      // Check for originalSource first (from the productCreateService), then fall back to src or filename
      const src = image.originalSource || image.src || image.filename;
      console.log('Processing image:', image);
      if (!src) {
        console.error('Missing image source URL');
        return null; // Skip missing URLs
      }
      
      // Store the original ID for tracking if available
      if (image.id || image.originalId) {
        originalIdMap.set(src, image.id || image.originalId);
      }
      
      // Don't validate URLs - Shopify can handle various URL formats
      // Just ensure we have a source URL
      return {
        originalSource: src,
        alt: image.alt !== undefined && image.alt !== null ? image.alt : "", // Use empty string as fallback
        mediaContentType: image.mediaContentType || "IMAGE"
        // Do not include originalId here as it's not supported by the API
      };
    }).filter(Boolean); // Remove null entries
    console.log('Prepared media items:', mediaItems);

    if (mediaItems.length === 0) {
      console.error('No valid media items to upload.');
      return [];
    }

    try {
      const mutation = `mutation productCreateMedia($productId: ID!, $media: [CreateMediaInput!]!) {
        productCreateMedia(productId: $productId, media: $media) {
          media {
            id
            alt
            mediaContentType
            status
            preview {
              image {
                url
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }`;

      const variables = {
        productId: productId,
        media: mediaItems
      };

      console.log('GraphQL mutation variables:', variables);

      const result = await this.graphqlService.mutate({
        query: mutation,
        variables
      });

      if (result?.productCreateMedia?.userErrors?.length > 0) {
        const errors = result.productCreateMedia.userErrors;
        console.error('Error uploading media:', JSON.stringify(errors, null, 2));
        throw new Error(errors.map(e => `${e.field}: ${e.message}`).join(', '));
      }

      // Enhance the returned media objects with original information for tracking
      const uploadedMedia = result.productCreateMedia.media.map((media, index) => {
        // Find the corresponding original image data
        const originalImage = index < images.length ? images[index] : null;
        return {
          ...media,
          // Include original source for better matching later
          originalSource: mediaItems[index]?.originalSource,
          // Include original ID if available
          originalId: originalIdMap.get(mediaItems[index]?.originalSource) || null
        };
      });

      console.log('Media uploaded successfully:', uploadedMedia);
      return uploadedMedia;
    } catch (error) {
      console.error('Error uploading product media:', error);
      throw error;
    }
  }

  /**
   * Checks if media is ready (status is SUCCESS or READY)
   * @param {string} mediaId - The ID of the media to check
   * @param {number} maxAttempts - Maximum number of attempts to check status
   * @returns {Promise<boolean>} Whether media is ready
   */
  async waitForMediaReady(mediaId, maxAttempts = 10) {
    const getMediaQuery = `
      query getMedia($mediaId: ID!) {
        node(id: $mediaId) {
          ... on MediaImage {
            id
            status
          }
        }
      }
    `;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`Checking media status (attempt ${attempt}/${maxAttempts})...`);
      
      const mediaResult = await this.graphqlService.query(getMediaQuery, { mediaId });
      const media = mediaResult?.node;
      
      if (!media) {
        console.log('Media not found');
        return false;
      }
      
      console.log(`Media status: ${media.status}`);
      
      // Accept both SUCCESS and READY as valid statuses
      if (media.status === 'SUCCESS' || media.status === 'READY') {
        console.log(`Media ${mediaId} is ready for use`);
        return true;
      }
      
      if (media.status === 'FAILED') {
        throw new Error('Media processing failed');
      }
      
      // Wait before checking again (1 second)
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    // If we've reached max attempts but the media is still in PROCESSING status,
    // we'll consider it ready enough to proceed rather than failing
    console.log(`Media ${mediaId} processing took too long, but we'll proceed anyway`);
    return true;
  }

  /**
   * Associates media with variants
   * @param {string} productId - The ID of the product
   * @param {Array} variantMedia - Array of variant-media associations
   * @param {Array} createdVariants - Optional array of created variants to validate against
   * @returns {Promise<object>} Result of the association
   */
  async associateMediaWithVariants(productId, variantMedia, createdVariants = []) {
    const associateMediaMutation = `
      mutation productVariantsBulkUpdate($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
        productVariantsBulkUpdate(productId: $productId, variants: $variants) {
          productVariants {
            id
            image {
              id
              url
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    // Ensure variantMedia is properly formatted
    if (!Array.isArray(variantMedia)) {
      console.warn('variantMedia is not an array, converting to array');
      variantMedia = Object.entries(variantMedia).map(([variantId, mediaId]) => ({
        id: variantId,
        mediaId: mediaId
      }));
    }

    // Validate each entry has the required fields
    const validVariantMedia = variantMedia.filter(vm => {
      if (!vm.id || (!vm.mediaId && !vm.media && !vm.imageSrc)) {
        console.warn('Invalid variant media entry:', vm);
        return false;
      }
      return true;
    });

    if (validVariantMedia.length === 0) {
      console.warn('No valid variant media entries found');
      return [];
    }

    // If we have createdVariants, validate that the variant IDs exist
    let validatedMedia = validVariantMedia;
    if (createdVariants && createdVariants.length > 0) {
      // Create a set of valid variant IDs for quick lookup
      const validVariantIds = new Set(createdVariants.map(v => v.id));
      console.log(`Validating variant IDs against ${validVariantIds.size} created variants`);
      
      validatedMedia = validVariantMedia.filter(vm => {
        const isValid = validVariantIds.has(vm.id);
        if (!isValid) {
          console.warn(`Skipping invalid variant ID: ${vm.id} - not found in created variants`);
        }
        return isValid;
      });
      
      console.log(`Filtered out ${validVariantMedia.length - validatedMedia.length} invalid variant IDs`);
    }

    if (validatedMedia.length === 0) {
      console.warn('No valid variant media entries found after validation');
      return [];
    }

    // Ensure each entry has the correct field name (mediaId)
    const formattedMedia = validatedMedia.map(vm => ({
      id: vm.id,
      mediaId: vm.mediaId || vm.media || vm.imageSrc
    }));

    console.log('Associating media with variants:', JSON.stringify(formattedMedia, null, 2));

    try {
      const result = await this.graphqlService.mutate(associateMediaMutation, {
        productId,
        variants: formattedMedia
      });

      if (result?.productVariantsBulkUpdate?.userErrors?.length > 0) {
        const errors = result.productVariantsBulkUpdate.userErrors;
        console.error('Errors associating media with variants:', JSON.stringify(errors, null, 2));
        // Don't throw, just log the error and return what we have
        return result?.productVariantsBulkUpdate?.productVariants || [];
      }

      return result?.productVariantsBulkUpdate?.productVariants || [];
    } catch (error) {
      console.error('Error in API call to associate media with variants:', error);
      // Return empty array instead of throwing
      return [];
    }
  }

  /**
   * Adds a single image to a product
   * @param {string} productId - The ID of the product
   * @param {object} imageInput - Image input data
   * @returns {Promise<object>} Created image data
   */
  async addImageToProduct(productId, imageInput) {
    console.log(`Adding image to product ${productId}`);
    console.log(`Image source: ${imageInput.src}`);
    console.log(`Image alt text: ${imageInput.alt || 'None provided'}`);
    
    const media = await this.uploadProductMedia(productId, [imageInput]);
    if (!media || media.length === 0) {
      throw new Error('Failed to upload image');
    }

    const mediaId = media[0].id;
    await this.waitForMediaReady(mediaId);

    return media[0];
  }

  /**
   * Uploads images and associates them with variants using tracking data
   * @param {string} productId - The ID of the product
   * @param {Array} images - Array of image inputs
   * @param {Array} createdVariants - The created variants
   * @param {Object} trackingService - The ShopifyVariantImageTrackingService instance
   * @returns {Promise<Array>} Created media objects with variant associations
   */
  async uploadImagesAndLinkToVariants(productId, images, createdVariants, trackingService) {
    console.log(`Uploading images and linking to variants for product ${productId}`);
    console.log(`Number of images: ${images.length}`);
    console.log(`Number of variants: ${createdVariants.length}`);

    // Ensure tracking service has the original images for reference
    if (trackingService && !trackingService.originalImages) {
      console.log('Setting original images in tracking service');
      trackingService.originalImages = images;
    }

    // Step 1: Upload all images
    const uploadedMedia = await this.uploadProductMedia(productId, images);
    console.log(`Successfully uploaded ${uploadedMedia.length} media items`);

    // Step 2: Wait for all media to be ready (with error handling)
    const readyMedia = [];
    for (const media of uploadedMedia) {
      try {
        const isReady = await this.waitForMediaReady(media.id);
        if (isReady) {
          readyMedia.push(media);
        }
      } catch (error) {
        console.warn(`Warning: Error waiting for media ${media.id} to be ready: ${error.message}`);
        // Still add the media to our list, as it might be usable despite the error
        readyMedia.push(media);
      }
    }

    console.log(`${readyMedia.length} of ${uploadedMedia.length} media items are ready for use`);
    
    // If no media is ready, return what we have but don't try to create associations
    if (readyMedia.length === 0) {
      console.log('No media is ready, skipping variant associations');
      return uploadedMedia;
    }

    // Step 3: Get image-variant associations from tracking service
    if (!trackingService) {
      console.log('No tracking service provided, using direct association');
      const directAssociations = await this.directVariantImageAssociation(productId, readyMedia, createdVariants);
      return uploadedMedia;
    }

    // If we have a tracking service, map the original images to the new media
    if (trackingService.originalImages && trackingService.originalImages.length > 0) {
      console.log('Mapping original images to new media in tracking service');
      trackingService.mapOriginalToNewMedia(trackingService.originalImages, readyMedia);
    }

    // Get the image-variant associations from the tracking service
    const associations = trackingService.getImageVariantAssociations(readyMedia, createdVariants);
    console.log(`Generated ${associations.length} image-variant associations`);

    // Check if we have any associations to process
    if (!associations || associations.length === 0) {
      console.log('No image-variant associations to process, trying direct association');
      // Attempt direct variant-image association as a fallback
      const directAssociations = await this.directVariantImageAssociation(productId, readyMedia, createdVariants);
      console.log(`Direct association fallback generated ${directAssociations.length} associations`);
      return uploadedMedia;
    }

    // Convert associations to the format expected by the Shopify API
    // The new format has imageId and variantIds arrays, we need to convert to id/mediaId pairs
    const formattedAssociations = [];
    
    associations.forEach(assoc => {
      console.log(`Processing association for image ${assoc.imageId} with ${assoc.variantIds.length} variants`);
      
      // For each variant ID in the association, create a separate entry
      assoc.variantIds.forEach(variantId => {
        formattedAssociations.push({
          id: variantId,
          mediaId: assoc.imageId
        });
        console.log(`Added association: Variant ${variantId} -> Media ${assoc.imageId}`);
      });
    });

    console.log(`Converted to ${formattedAssociations.length} individual variant-media associations`);
    
    // Step 4: Associate media with variants
    try {
      console.log('Calling associateMediaWithVariants with formatted associations');
      const result = await this.associateMediaWithVariants(productId, formattedAssociations, createdVariants);
      console.log('Successfully associated media with variants:', result);
    } catch (error) {
      console.error('Error associating media with variants:', error);
      // Continue and return the uploaded media even if associations fail
    }

    return uploadedMedia;
  }

  /**
   * Directly associates images with variants based on position/index
   * This is a fallback method when the tracking service doesn't produce associations
   * @param {string} productId - The ID of the product
   * @param {Array} uploadedMedia - The uploaded media items
   * @param {Array} createdVariants - The created variants
   * @returns {Promise<Array>} - The created associations
   */
  async directVariantImageAssociation(productId, uploadedMedia, createdVariants) {
    console.log('Attempting direct variant-image association based on position/index');
    
    if (!uploadedMedia || !createdVariants || uploadedMedia.length === 0 || createdVariants.length === 0) {
      console.log('Missing required data for direct association');
      return [];
    }
    
    const associations = [];
    
    // Calculate how many variants should be assigned to each image
    const variantsPerImage = Math.ceil(createdVariants.length / uploadedMedia.length);
    console.log(`Using position-based association with ${variantsPerImage} variants per image`);
    
    // Assign variants to images based on position
    uploadedMedia.forEach((media, imageIndex) => {
      // Calculate which variants should be assigned to this image
      const startIdx = imageIndex * variantsPerImage;
      const endIdx = Math.min(startIdx + variantsPerImage, createdVariants.length);
      
      // Get the variants for this image
      const imageVariants = createdVariants.slice(startIdx, endIdx);
      
      if (imageVariants.length > 0) {
        console.log(`Associating media ${media.id} (index ${imageIndex}) with variants ${startIdx}-${endIdx-1} (${imageVariants.length} variants)`);
        
        imageVariants.forEach(variant => {
          associations.push({
            id: variant.id,
            mediaId: media.id
          });
        });
      }
    });
    
    console.log(`Created ${associations.length} direct associations by position`);
    
    // If we found associations, apply them
    if (associations.length > 0) {
      try {
        await this.associateMediaWithVariants(productId, associations, createdVariants);
        console.log('Successfully applied direct associations');
      } catch (error) {
        console.error('Error applying direct associations:', error);
      }
    }
    
    return associations;
  }
}

// Helper function to validate URLs
function isValidUrl(url) {
  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
}
